import { useEffect, useSyncExternalStore } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { brandOriginUrl } from '@/api';
import {
  casinoCategoryPath,
  casinoGameThumbUrl,
  casinoNavSlug,
  casinoPlayPath,
  casinoVendorLogoUrl,
  type CasinoGame,
} from '@/domain';
import {
  clearFreeSpinsBonus,
  getFreeSpinsBonus,
  getFreeSpinsBonusVersion,
  subscribeFreeSpinsBonus,
  type FreeSpinsBonusGame,
  type FreeSpinsBonusNotification,
} from '@/player';
import { Button, Icon, Modal, Toast } from '@/ui/common';

const AUTO_HIDE_MS = 10_000;

function freeSpinsGameAsCasinoGame(
  game: FreeSpinsBonusGame,
  providerName: string,
): CasinoGame {
  return {
    Name: game.Name,
    GameTag: game.GameTag,
    VendorName: game.VendorName || providerName,
    ProviderName: providerName,
    ProviderGameId: 0,
    CategoryName: 'slots',
  };
}

function FreeSpinsGameCard({
  game,
  providerName,
  freeRounds,
  onNavigate,
}: {
  game: FreeSpinsBonusGame;
  providerName: string;
  freeRounds: number;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const origin = brandOriginUrl();
  const casinoGame = freeSpinsGameAsCasinoGame(game, providerName);
  const thumb = casinoGameThumbUrl(origin, casinoGame);
  const logoSrc = casinoVendorLogoUrl(origin, casinoGame.VendorName);
  const to = casinoPlayPath(casinoGame);

  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="flex w-full items-center gap-3 rounded-md border border-border-subtle bg-bg-elevated p-3 text-left transition-opacity hover:opacity-90"
    >
      <div className="h-[85px] w-16 shrink-0 overflow-hidden rounded-md bg-bg-surface">
        <img
          src={thumb}
          alt=""
          className="size-full object-cover"
          onError={(event) => {
            event.currentTarget.style.visibility = 'hidden';
          }}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="w-full text-[18px] font-bold leading-normal text-text-primary">
          {game.Name}
        </p>
        <div className="flex w-full items-center gap-2">
          <div className="relative flex h-8 w-16 shrink-0 items-center justify-center overflow-hidden">
            <img
              src={logoSrc}
              alt={providerName || casinoGame.VendorName}
              className="max-h-8 max-w-full object-contain"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <span className="rounded-md bg-action-primary/15 px-2 py-1 text-label-s font-bold text-action-primary">
            {freeRounds} {t('FreeSpins', { defaultValue: 'Free Spins' })}
          </span>
        </div>
      </div>
      <Icon name="arrow-right" size={16} className="shrink-0 text-icon-default" />
    </Link>
  );
}

function FreeSpinsBonusModal({
  notification,
  onClose,
}: {
  notification: FreeSpinsBonusNotification;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const providerSlug = casinoNavSlug(notification.ProviderName);
  const fallbackTo = casinoCategoryPath('slots', providerSlug || undefined);
  const hasGames = notification.Games.length > 0;

  return (
    <Modal
      open
      dismissible={false}
      onClose={onClose}
      aria-label={t('FreeSpins', { defaultValue: 'Free Spins' })}
      panelClassName="w-full max-w-md rounded-lg border border-border-subtle bg-bg-surface p-4 shadow-lg"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-1 text-center">
          <Icon name="freespins" size={36} className="text-action-primary" />
          <h2 className="text-heading-m font-bold text-text-primary">
            {t('Congratulations', { defaultValue: 'Congratulations!' })}
          </h2>
          <p className="text-body-m text-text-secondary">
            {t('YouWon', { defaultValue: 'You WON' })}{' '}
            <span className="font-bold text-text-primary">
              {notification.FreeRounds}{' '}
              {t('FreeSpins', { defaultValue: 'Free Spins' })}
            </span>
          </p>
          {notification.Title ? (
            <p className="text-label-s text-text-secondary">{notification.Title}</p>
          ) : null}
        </div>

        {hasGames ? (
          <div className="flex flex-col gap-2">
            {notification.Games.map((game) => (
              <FreeSpinsGameCard
                key={`${game.GameTag}-${game.VendorName}`}
                game={game}
                providerName={notification.ProviderName}
                freeRounds={notification.FreeRounds}
                onNavigate={onClose}
              />
            ))}
          </div>
        ) : (
          <Link
            to={fallbackTo}
            onClick={onClose}
            className="flex w-full items-center justify-center rounded-md border border-border-subtle bg-bg-elevated px-3 py-3 text-body-m font-bold text-action-primary transition-opacity hover:opacity-90"
          >
            {t('Play', { defaultValue: 'Play' })}
          </Link>
        )}

        <Button variant="secondary" fullWidth onClick={onClose}>
          {t('Close', { defaultValue: 'Close' })}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Legacy FreeSpinsWon + PNotify for PlayerMessage Type `FreeSpinsBonus`.
 */
export function FreeSpinsBonusHost() {
  const { t } = useTranslation();
  const version = useSyncExternalStore(
    subscribeFreeSpinsBonus,
    getFreeSpinsBonusVersion,
    getFreeSpinsBonusVersion,
  );
  const notification = version > 0 ? getFreeSpinsBonus() : null;

  useEffect(() => {
    if (!notification) return;
    const timer = window.setTimeout(() => clearFreeSpinsBonus(), AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [notification?.id, notification?.at]);

  if (!notification) return null;

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-16 z-[60] flex justify-center px-4">
        <div className="pointer-events-auto w-full max-w-md">
          <Toast tone="success" onDismiss={clearFreeSpinsBonus}>
            {t('NewMessage', { defaultValue: 'New message' })}:{' '}
            {t('FreeSpins', { defaultValue: 'Free Spins' })}
          </Toast>
        </div>
      </div>
      <FreeSpinsBonusModal
        notification={notification}
        onClose={clearFreeSpinsBonus}
      />
    </>
  );
}
